// Magic Link Authentication Module
// Users authenticate via magic link tokens only

import { CONFIG } from '../config.js';
import { fetchPlans, getPlanDisplayName } from './plansService.js';

const API_BASE = CONFIG.AUTH_API_BASE;
const CHILD_ME_FALLBACK_BASES = [API_BASE, CONFIG.API_BASE];

async function logApiDebug(label, requestUrl, response) {
    const responseClone = response.clone();
    const bodySnippet = await responseClone.text()
        .then(text => text.slice(0, 300))
        .catch(() => '[body unreadable]');

    console.log(`[auth debug] ${label}`, {
        requestUrl,
        responseUrl: response.url,
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
        server: response.headers.get('server'),
        via: response.headers.get('via'),
        xVercelId: response.headers.get('x-vercel-id'),
        bodySnippet
    });
}

async function fetchChildMe(headers) {
    const attemptedUrls = new Set();
    let lastNotFoundResponse = null;

    for (const base of CHILD_ME_FALLBACK_BASES) {
        const url = `${base}/api/child/me`;
        if (attemptedUrls.has(url)) continue;
        attemptedUrls.add(url);

        const response = await fetch(url, {
            method: 'GET',
            headers
        });

        await logApiDebug('fetchChildMe', url, response);

        if (response.status === 404) {
            lastNotFoundResponse = response;
            continue;
        }

        return response;
    }

    return lastNotFoundResponse;
}

// Session storage keys
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

// Current user state
let currentUser = null;
let authToken = null;

function mapChildUser(userObj = {}) {
    return {
        id: userObj.id || userObj.user_id || null,
        username: userObj.username || null,
        email: userObj.email || null,
        firstName: userObj.first_name || userObj.firstName || null,
        lastName: userObj.last_name || userObj.lastName || null,
        plan: userObj.plan || null,
        planName: userObj.plan_name || userObj.planName || null,
        country_id: userObj.country_id || null,
        country_name: userObj.country_name || null,
        region_id: userObj.region_id || null,
        region_name: userObj.region_name || null,
        school_type_id: userObj.school_type_id || null,
        school_type_name: userObj.school_type_name || null,
        school_type_level: userObj.school_type_level || null,
        grade_id: userObj.grade_id || null,
        grade_name: userObj.grade_name || null,
        isChild: true
    };
}

/**
 * Initialize authentication on page load
 */
export async function initAuth() {
    console.log('Initializing Simple Auth...');

    // If there's a magic link token in the URL, skip session restoration
    // to avoid a race condition where logout() triggers a reload that
    // consumes the one-time token before the login response arrives
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('token')) {
        console.log('Magic link token detected in URL, skipping session restoration');
        authToken = null;
        currentUser = null;
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        updateUIForLoggedOutUser();
        return;
    }

    // Try to restore session from localStorage
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
        try {
            authToken = storedToken;
            currentUser = JSON.parse(storedUser);
            console.log('Session restored for user:', currentUser.username || currentUser.email || currentUser.id || '[unknown]');

            // Fetch fresh user profile from API to get current plan
            await fetchUserProfile();

            // Preload plans data for display names
            await fetchPlans();

            updateUIForLoggedInUser();
        } catch (e) {
            console.error('Error restoring session:', e);
            logout();
        }
    } else {
        console.log('No existing session found');
        updateUIForLoggedOutUser();
    }
}

/**
 * Fetch user profile from API to get current plan info
 */
async function fetchUserProfile() {
    if (!authToken) return;

    try {
        const headers = {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
        };
        const response = await fetchChildMe(headers);

        if (!response) {
            console.warn('fetchUserProfile: child/me returned no response, using cached data');
            return;
        }

        if (!response.ok) {
            if (response.status === 401) {
                console.log('Token expired, logging out...');
                logout();
                return;
            }

            if (response.status === 404) {
                console.warn('fetchUserProfile: child/me returned 404; the token is valid but no child profile was found on the backend. Using cached data.');
                return;
            }

            console.warn(`fetchUserProfile: HTTP ${response.status}, using cached data`);
            return;
        }

        const userObj = await response.json();

        // Update currentUser with fresh data from server
        currentUser = {
            ...currentUser,
            ...mapChildUser(userObj)
        };

        // Save updated user data to localStorage
        localStorage.setItem(USER_KEY, JSON.stringify(currentUser));

        // Update credit display with remaining hours from API
        updateCreditDisplay(userObj);

        console.log('User profile updated, plan:', currentUser.planName || currentUser.plan);
    } catch (error) {
        console.error('Error fetching user profile:', error);
        // Don't logout on network errors, just use cached data
    }
}

/**
 * Update credit display with data from /api/child/me response
 * Fields: daily_limit_minutes, seconds_used, remaining_seconds (null = unlimited)
 */
function updateCreditDisplay(userObj) {
    const creditMinutesEl = document.getElementById('creditMinutes');
    const creditBarFill = document.getElementById('creditBarFill');
    if (!creditMinutesEl) return;

    const dailyLimitMinutes = userObj.daily_limit_minutes;
    const remainingSeconds = userObj.remaining_seconds;

    // Unlimited plan (remaining_seconds is null)
    if (remainingSeconds === null || remainingSeconds === undefined) {
        creditMinutesEl.textContent = '∞';
        if (creditBarFill) {
            creditBarFill.style.width = '100%';
            creditBarFill.style.background = '#22c55e';
        }
        console.log('Credit display: Unlimited plan');
        return;
    }

    // Limited plan - show remaining time in hours
    const remainingHours = remainingSeconds / 3600;
    const totalHours = (dailyLimitMinutes || 720) / 60; // fallback 12h

    // Format display
    let displayText;
    if (remainingHours >= 1) {
        displayText = `${Math.round(remainingHours * 10) / 10} h`;
    } else {
        // Less than 1 hour - show minutes
        const remainingMin = Math.round(remainingSeconds / 60);
        displayText = `${remainingMin} min`;
    }
    creditMinutesEl.textContent = displayText;

    // Update progress bar
    const percent = Math.min(100, Math.max(0, (remainingHours / totalHours) * 100));
    if (creditBarFill) {
        creditBarFill.style.width = `${percent}%`;
        if (percent > 50) {
            creditBarFill.style.background = '#22c55e';
        } else if (percent > 20) {
            creditBarFill.style.background = '#eab308';
        } else {
            creditBarFill.style.background = '#ef4444';
        }
    }
    console.log(`Credit display: ${displayText} (${percent.toFixed(0)}% remaining)`);
}

/**
 * Login with Magic Link token (for child/student users)
 * @param {string} token - The magic link token from URL
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function loginWithMagicLink(token) {
    console.log('Attempting Magic Link login...');

    try {
        const response = await fetch(`${API_BASE}/api/child/magic-login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ token })
        });

        if (!response.ok) {
            if (response.status === 401) {
                return {
                    success: false,
                    error: window.i18n?.t('auth.magicLinkInvalid') || 'The login link is invalid or expired.',
                    isInvalidToken: true
                };
            }

            if (response.status === 429) {
                return {
                    success: false,
                    error: window.i18n?.t('auth.rateLimited') || 'Too many login attempts. Please wait a moment.',
                    isRateLimited: true
                };
            }

            const error = await response.json().catch(() => ({}));
            return {
                success: false,
                error: error.detail || error.message || 'Magic Link login failed'
            };
        }

        const data = await response.json();

        // Store token and user info
        authToken = data.token;

        currentUser = mapChildUser(data.user);

        localStorage.setItem(TOKEN_KEY, authToken);
        localStorage.setItem(USER_KEY, JSON.stringify(currentUser));

        console.log('Magic Link login successful for:', currentUser.username || currentUser.email || currentUser.id || '[unknown]');

        // Update UI
        updateUIForLoggedInUser();

        return { success: true };

    } catch (error) {
        console.error('Magic Link login error:', error);
        return {
            success: false,
            error: window.i18n?.t('auth.connectionError') || 'Connection error. Please try again.'
        };
    }
}


/**
 * Logout the current user
 */
export function logout() {
    authToken = null;
    currentUser = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);

    console.log('User logged out');

    // Reload page
    window.location.reload();
}

/**
 * Check if user is signed in
 * @returns {boolean}
 */
export function isUserSignedIn() {
    return !!authToken && !!currentUser;
}

/**
 * Get the current auth token (JWT)
 * @returns {string|null}
 */
export function getToken() {
    return authToken;
}

/**
 * Get current user info
 * @returns {object|null}
 */
export function getCurrentUser() {
    return currentUser;
}

/**
 * Handle authentication errors from API responses
 * Call this from any API module when receiving 401 or 429 responses
 * @param {Response} response - The fetch Response object
 * @returns {Promise<{handled: boolean, error?: string}>}
 */
export async function handleAuthError(response) {
    if (response.status === 401) {
        // Token expired or invalid - clear auth and redirect to login
        console.log('Auth Error: Token expired or invalid (401). Redirecting to login...');

        // Clear stored auth
        authToken = null;
        currentUser = null;
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);

        // Show toast notification
        if (window.showToast) {
            window.showToast(
                window.i18n?.t('auth.sessionExpired') || 'Your session has expired. Please use a new magic link.',
                'warning'
            );
        }

        // Update UI - no login modal, user needs new magic link
        updateUIForLoggedOutUser();

        return {
            handled: true,
            error: window.i18n?.t('auth.sessionExpired') || 'Session expired'
        };
    }

    if (response.status === 429) {
        // Rate limited
        console.log('Auth Error: Rate limited (429)');

        if (window.showToast) {
            window.showToast(
                window.i18n?.t('auth.rateLimited') || 'Too many requests. Please wait a moment.',
                'warning'
            );
        }

        return {
            handled: true,
            error: window.i18n?.t('auth.rateLimited') || 'Too many requests'
        };
    }

    return { handled: false };
}

/**
 * Update UI for logged in state
 */
function updateUIForLoggedInUser() {
    const userButtonDiv = document.getElementById('userButton');
    if (!userButtonDiv) return;

    // Clear existing content
    userButtonDiv.innerHTML = '';

    // Show credit display
    const creditDisplay = document.getElementById('creditDisplay');
    if (creditDisplay) {
        creditDisplay.style.display = 'flex';
    }

    // Show chat icon for logged in users (except free plan)
    const chatIcon = document.getElementById('chatIcon');
    const chatHistoryBtn = document.getElementById('chatHistoryBtn');
    if (chatIcon) {
        if (currentUser && currentUser.plan !== 'Free') {
            chatIcon.style.display = 'flex';
            if (chatHistoryBtn) chatHistoryBtn.style.display = 'flex';
        } else {
            console.log('User is on free plan, hiding chat icon');
            chatIcon.style.display = 'none';
            if (chatHistoryBtn) chatHistoryBtn.style.display = 'none';
        }
    }

    // Create profile menu
    createProfileMenu(userButtonDiv);
}

/**
 * Update UI for logged out state
 * Note: No login button shown - users must use magic link to authenticate
 */
function updateUIForLoggedOutUser() {
    const userButtonDiv = document.getElementById('userButton');
    if (!userButtonDiv) return;

    // Clear existing content
    userButtonDiv.innerHTML = '';

    // Hide credit display
    const creditDisplay = document.getElementById('creditDisplay');
    if (creditDisplay) {
        creditDisplay.style.display = 'none';
    }

    // Hide chat icon for logged out users
    const chatIcon = document.getElementById('chatIcon');
    if (chatIcon) {
        chatIcon.style.display = 'none';
    }

    // Hide chat modal if open
    const chatModal = document.getElementById('chatModal');
    if (chatModal) {
        chatModal.classList.add('hidden');
    }

    // No login button - authentication only via magic link
}

/**
 * Create the profile menu dropdown
 */
function createProfileMenu(container) {
    const profileContainer = document.createElement('div');
    profileContainer.className = 'profile-menu-container';

    // Profile image button with initials
    const profileBtn = document.createElement('div');
    profileBtn.className = 'profile-image-btn';
    profileBtn.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #00e5ff;
        color: #000;
        font-weight: bold;
        border-radius: 50%;
        width: 32px;
        height: 32px;
        cursor: pointer;
    `;

    // Use first name initial, fallback to username initial
    const initial = currentUser?.firstName?.charAt(0).toUpperCase()
        || currentUser?.username?.charAt(0).toUpperCase()
        || '?';
    profileBtn.textContent = initial;

    // Dropdown menu
    const dropdown = document.createElement('div');
    dropdown.className = 'profile-dropdown';

    // Name header - show full name if available, otherwise username
    const dropdownHeader = document.createElement('div');
    dropdownHeader.className = 'profile-dropdown-header';
    const displayName = (currentUser?.firstName && currentUser?.lastName)
        ? `${currentUser.firstName} ${currentUser.lastName}`
        : currentUser?.username || 'Unknown';
    const usernameText = document.createElement('p');
    usernameText.className = 'profile-dropdown-email';
    usernameText.textContent = displayName;
    dropdownHeader.appendChild(usernameText);

    // Show Plan Name - use dynamic name from plans API
    const planId = currentUser?.plan || null;
    const displayPlan = getPlanDisplayName(planId);

    if (displayPlan) {
        const planText = document.createElement('p');
        planText.className = 'profile-dropdown-plan';
        // Improved readability: brighter color, slightly larger font, bold weight
        planText.style.cssText = 'color: var(--text-primary, #ffffff); font-size: 0.9em; font-weight: 600; margin-top: 4px; letter-spacing: 0.5px;';

        const planLabel = window.i18n ? window.i18n.t('profile.plan') : 'Plan: ';
        planText.textContent = `${planLabel}${displayPlan}`;

        dropdownHeader.appendChild(planText);
    }

    dropdown.appendChild(dropdownHeader);

    // My Learning Progress button
    const progressBtn = document.createElement('button');
    progressBtn.className = 'profile-menu-item';
    progressBtn.setAttribute('data-i18n', 'profile.myProgress');
    progressBtn.textContent = window.i18n ? window.i18n.t('profile.myProgress') : 'My Learning Progress';
    progressBtn.onclick = () => {
        console.log('Navigate to learning progress');
    };
    dropdown.appendChild(progressBtn);



    // Divider
    const divider = document.createElement('div');
    divider.className = 'profile-dropdown-divider';
    dropdown.appendChild(divider);

    // Sign Out button
    const signOutBtn = document.createElement('button');
    signOutBtn.className = 'profile-menu-item';
    signOutBtn.setAttribute('data-i18n', 'profile.signOut');
    signOutBtn.textContent = window.i18n ? window.i18n.t('profile.signOut') : 'Sign Out';
    signOutBtn.onclick = () => {
        logout();
    };
    dropdown.appendChild(signOutBtn);

    profileContainer.appendChild(profileBtn);
    profileContainer.appendChild(dropdown);
    container.appendChild(profileContainer);
}
